import { safeInject } from '@wui/common-library'
import {
  HistoryMessageItem,
  isMessageNotReactionOrNotification,
  MessageCallReceived,
} from '@wui/im'
import { Emitter } from 'mitt'
import { nextTick, onScopeDispose, ref } from 'vue'

import { chatModel, LAZY_LOAD_NEWER_BATCH_SIZE } from '@/entities/messages'

import { Events } from '@/shared/types/app'

import { useChatHistoryLoading } from './use-chat-history-loading'

const MAX_INVISIBLE_HISTORY_CHAIN = 5

const isDisplayableChatEvent = (event: HistoryMessageItem): boolean => {
  return isMessageNotReactionOrNotification(event)
    && (event.item as MessageCallReceived).event !== 'call_record_completed'
}

type UseChatLazyLoadNewerHooks = {
  onLoadCooldownEnd?: () => void
  prepareAppend?: () => void
  afterAppend?: () => void | Promise<void>
}

export const useChatLazyLoadNewer = (
  pServed: string,
  disableHistoryLoading = false,
  hooks?: UseChatLazyLoadNewerHooks,
) => {
  const { loading, startLoading, stopLoading } = useChatHistoryLoading()
  const {
    findRoomMessages,
    fetchHistoryNewer,
    addMessages,
    findById,
    getRoomHistoryViewport,
    updateViewportAfterNewerLoad,
  } = chatModel.useChatMessageStore()

  const emitter = safeInject<Emitter<Events>>('emitter')
  const isEnabled = ref(false)
  const coolDown = ref(false)
  let coolDownTimer: ReturnType<typeof setTimeout> | null = null
  let pendingHistory: HistoryMessageItem[] | null = null
  let prefetchPromise: Promise<void> | null = null
  let isDisposed = false
  let isLoadInFlight = false

  const hasMoreNewerHistory = (): boolean => {
    const viewport = getRoomHistoryViewport(pServed)
    return viewport.mode === 'anchored' && viewport.hasMoreNewer
  }

  const countDisplayableMessages = (): number => {
    return findRoomMessages(pServed).filter(isDisplayableChatEvent).length
  }

  const countNewHistoryMessages = (messages: HistoryMessageItem[]): number => {
    return messages.filter((message) => message.eventId && !findById(message.eventId)).length
  }

  const clearPendingHistory = (): void => {
    pendingHistory = null
    prefetchPromise = null
  }

  const appendHistory = (messages: HistoryMessageItem[]): void => {
    if (messages.length) {
      addMessages(pServed, messages)
      updateViewportAfterNewerLoad(pServed, messages, LAZY_LOAD_NEWER_BATCH_SIZE)
    }
  }

  const prefetchEvents = (): void => {
    if (
      isDisposed
      || disableHistoryLoading
      || loading.value
      || coolDown.value
      || prefetchPromise
      || pendingHistory?.length
      || !hasMoreNewerHistory()
    ) {
      return
    }

    prefetchPromise = fetchHistoryNewer(pServed, LAZY_LOAD_NEWER_BATCH_SIZE)
      .then((history) => {
        if (history.length) {
          pendingHistory = history
        }
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        prefetchPromise = null
      })
  }

  const startCoolDown = (onComplete?: () => void): void => {
    coolDown.value = true
    if (coolDownTimer) {
      clearTimeout(coolDownTimer)
    }
    coolDownTimer = setTimeout(() => {
      coolDown.value = false
      coolDownTimer = null
      onComplete?.()
    }, 800)
  }

  const loadEvents = () => {
    if (isDisposed || isLoadInFlight || loading.value || disableHistoryLoading || coolDown.value || !hasMoreNewerHistory()) {
      return
    }

    isLoadInFlight = true
    const loadHistory = async (invisibleChain = 0): Promise<void> => {
      if (prefetchPromise) {
        await prefetchPromise
      }
      if (isDisposed) {
        return
      }

      const cached = pendingHistory
      pendingHistory = null
      const needsNetwork = !cached?.length
      const displayableBefore = countDisplayableMessages()

      if (needsNetwork) {
        startLoading()
      }

      let newMessagesCount = 0
      let fetchedHistory: HistoryMessageItem[] = []
      try {
        if (cached?.length) {
          fetchedHistory = cached
        } else {
          fetchedHistory = await fetchHistoryNewer(pServed, LAZY_LOAD_NEWER_BATCH_SIZE)
        }

        if (isDisposed) {
          return
        }

        newMessagesCount = countNewHistoryMessages(fetchedHistory)
        if (fetchedHistory.length) {
          hooks?.prepareAppend?.()
          try {
            appendHistory(fetchedHistory)
            await nextTick()
          } finally {
            await hooks?.afterAppend?.()
          }
        }
      } finally {
        if (needsNetwork) {
          stopLoading()
        }
      }

      const displayableAfter = countDisplayableMessages()
      const addedVisibleMessages = displayableAfter > displayableBefore

      if (newMessagesCount > 0 && addedVisibleMessages) {
        startCoolDown(() => {
          hooks?.onLoadCooldownEnd?.()
        })
      }

      await nextTick()
      if (isDisposed) {
        return
      }

      if (
        newMessagesCount > 0
        && !addedVisibleMessages
        && hasMoreNewerHistory()
        && invisibleChain < MAX_INVISIBLE_HISTORY_CHAIN
      ) {
        await loadHistory(invisibleChain + 1)
        return
      }

      if (hasMoreNewerHistory() && newMessagesCount > 0 && !addedVisibleMessages) {
        hooks?.onLoadCooldownEnd?.()
      }
    }

    return loadHistory().finally(() => {
      isLoadInFlight = false
    })
  }

  const enable = () => {
    if (isDisposed || isEnabled.value || disableHistoryLoading) {
      return
    }
    emitter.on('scrolledNearBottom', prefetchEvents)
    emitter.on('scrolledNearBottom', loadEvents)
    emitter.on('scrolledToDown', loadEvents)
    isEnabled.value = true
  }

  const disable = () => {
    if (isEnabled.value) {
      emitter.off('scrolledNearBottom', prefetchEvents)
      emitter.off('scrolledNearBottom', loadEvents)
      emitter.off('scrolledToDown', loadEvents)
      isEnabled.value = false
    }
    if (coolDownTimer) {
      clearTimeout(coolDownTimer)
      coolDownTimer = null
    }
    coolDown.value = false
    clearPendingHistory()
  }

  onScopeDispose(() => {
    isDisposed = true
    disable()
  })

  return {
    enable,
    disable,
    clearPendingHistory,
  }
}
